// 🔥 UPDATED VoicePreferenceService with better async handling

import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../../../firebase/firebase';

/**
 * VoicePreferenceService - Manages user voice preferences with Firestore
 */
class VoicePreferenceService {
  constructor(firestoreService = null) {
    this.firestoreService = firestoreService;
    this.voiceConfigs = {
            emma: {
        id: 'emma',
        name: 'Emma',
        personality: 'Warm & Empathetic',
        description: 'A caring, nurturing voice perfect for emotional support and personal reflection.',
        configId: process.env.REACT_APP_HUME_CONFIG_ID_EMMA || '1c825d5e-2276-4205-9f02-b4f21fe65ec3',
        color: '#EC4899',
        avatar: '💝',
        theme: 'empathetic'
      },
      bob: {
        id: 'bob',
        name: 'Bob',
        personality: 'Funny & Supportive',
        description: 'A balanced, professional voice that\'s great for work conversations and goal setting.',
        configId: process.env.REACT_APP_HUME_CONFIG_ID_BOB || 'a4962211-e08f-4fd2-9ec2-95bc68cb4f2b',
        color: '#4F46E5',
        avatar: '🎯',
        theme: 'professional'
      },
      alex: {
        id: 'alex',
        name: 'Alex',
        personality: 'Gen z & Casual Vibe',
        description: 'A balanced, professional voice that\'s great for work conversations and goal setting.',
        configId: process.env.REACT_APP_HUME_CONFIG_ID_ALEX || '24956749-78fe-48a9-a803-0a32a96a7a18',
        color: '#4F46E5',
        avatar: '🎯',
        theme: 'professional'
      },
      sage: {
        id: 'sage',
        name: 'Sage',
        personality: 'Wise & Thoughtful',
        description: 'A contemplative, insightful voice ideal for deep conversations and self-discovery.',
        configId: process.env.REACT_APP_HUME_CONFIG_ID_SAGE || 'dab06340-fd3c-4461-96f7-30dc17bcbb14',
        color: '#059669',
        avatar: '🧘',
        theme: 'contemplative'
      }
    };
  }

  /**
   * 🔥 NEW: Synchronous method to check localStorage immediately
   */
  hasVoicePreference() {
    try {
      // Check localStorage first for immediate response
      const localPreference = localStorage.getItem('preptune-voice-preference');
      if (localPreference) {
        const parsed = JSON.parse(localPreference);
        return !!(parsed && parsed.voiceId && this.voiceConfigs[parsed.voiceId]);
      }
      return false;
    } catch (error) {
      console.error('Error checking localStorage voice preference:', error);
      return false;
    }
  }

  /**
   * 🔥 UPDATED: Synchronous method to get current preference immediately
   */
  getCurrentVoicePreference() {
    try {
      const stored = localStorage.getItem('preptune-voice-preference');
      if (!stored) return null;
      
      const preference = JSON.parse(stored);
      
      // Validate that the voice still exists in our configs
      if (!this.voiceConfigs[preference.voiceId]) {
        console.warn(`Voice ${preference.voiceId} no longer exists, clearing preference`);
        localStorage.removeItem('preptune-voice-preference');
        return null;
      }
      
      return {
        ...this.voiceConfigs[preference.voiceId],
        ...preference
      };
    } catch (error) {
      console.error('Error reading localStorage voice preference:', error);
      return null;
    }
  }

  /**
   * 🔥 NEW: Get current config ID synchronously
   */
  getCurrentConfigId() {
    const preference = this.getCurrentVoicePreference();
    return preference?.configId || process.env.REACT_APP_HUME_CONFIG_ID || 'default-config';
  }

  /**
   * 🔥 FIXED: Synchronous save to localStorage, async save to Firestore
   */
  saveVoicePreference(voiceId) {
    try {
      if (!this.voiceConfigs[voiceId]) {
        throw new Error(`Voice ${voiceId} does not exist`);
      }

      const preference = {
        voiceId,
        name: this.voiceConfigs[voiceId].name,
        configId: this.voiceConfigs[voiceId].configId,
        selectedAt: new Date().toISOString(),
        version: '1.0'
      };

      // Always save to localStorage immediately (synchronous)
      localStorage.setItem('preptune-voice-preference', JSON.stringify(preference));
      console.log(`💾 Voice preference saved to localStorage: ${voiceId}`);

      // Save to Firestore in background (don't await)
      this.saveToFirestore(preference).catch(error => {
        console.error('Background Firestore save failed:', error);
      });

      return preference;
    } catch (error) {
      console.error('Error saving voice preference:', error);
      throw error;
    }
  }

  /**
   * 🔥 NEW: Async Firestore save method
   */
  async saveToFirestore(preference) {
    try {
      if (!auth.currentUser) {
        console.log('⏳ User not authenticated, skipping Firestore save');
        return;
      }

      const userDocRef = doc(db, 'users', auth.currentUser.uid);
      await updateDoc(userDocRef, {
        'settings.preferredVoice': preference.voiceId,
        'settings.preferredVoiceName': preference.name,
        'settings.preferredVoiceConfigId': preference.configId,
        'settings.voiceSelectedAt': preference.selectedAt,
        'settings.firstTimeCompleted': true,
        lastActiveAt: serverTimestamp()
      });

      console.log(`✅ Voice preference saved to Firestore: ${preference.voiceId}`);
    } catch (error) {
      console.error('Failed to save to Firestore:', error);
      throw error;
    }
  }

  /**
   * 🔥 NEW: Load from Firestore and sync with localStorage
   */
  async loadFromFirestore() {
    try {
      if (!auth.currentUser) {
        console.log('⏳ User not authenticated, skipping Firestore load');
        return null;
      }

      const userDocRef = doc(db, 'users', auth.currentUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        console.log('ℹ️ No user document in Firestore');
        return null;
      }

      const userData = userDoc.data();
      const voiceId = userData.settings?.preferredVoice;

      if (!voiceId || !this.voiceConfigs[voiceId]) {
        console.log('ℹ️ No valid voice preference in Firestore');
        return null;
      }

      const preference = {
        voiceId,
        name: this.voiceConfigs[voiceId].name,
        configId: this.voiceConfigs[voiceId].configId,
        selectedAt: userData.settings?.voiceSelectedAt || new Date().toISOString(),
        version: '1.0'
      };

      // Update localStorage cache
      localStorage.setItem('preptune-voice-preference', JSON.stringify(preference));
      console.log(`🔄 Synced Firestore preference to localStorage: ${voiceId}`);

      return {
        ...this.voiceConfigs[voiceId],
        ...preference
      };
    } catch (error) {
      console.error('Failed to load from Firestore:', error);
      return null;
    }
  }

  /**
   * Clear voice preference from both storage systems
   */
  async clearVoicePreference() {
    try {
      // Clear localStorage immediately
      localStorage.removeItem('preptune-voice-preference');
      console.log('🧹 Voice preference cleared from localStorage');

      // Clear Firestore in background
      if (auth.currentUser) {
        const userDocRef = doc(db, 'users', auth.currentUser.uid);
        await updateDoc(userDocRef, {
          'settings.preferredVoice': null,
          'settings.preferredVoiceName': null,
          'settings.preferredVoiceConfigId': null,
          'settings.voiceSelectedAt': null,
          lastActiveAt: serverTimestamp()
        });
        console.log('🧹 Voice preference cleared from Firestore');
      }
    } catch (error) {
      console.error('Error clearing voice preference:', error);
    }
  }

  /**
   * Get all available voice configs
   */
  getAllVoiceConfigs() {
    return Object.values(this.voiceConfigs);
  }

  /**
   * Get config for a specific voice
   */
  getVoiceConfig(voiceId) {
    return this.voiceConfigs[voiceId] || null;
  }

  /**
   * 🔥 NEW: Get voice by ID (used by main component)
   */
  getVoiceById(voiceId) {
    return this.voiceConfigs[voiceId] || null;
  }

  /**
   * Check if this is the user's first time (no voice selected)
   */
  isFirstTimeUser() {
    return !this.hasVoicePreference();
  }

  /**
   * 🔥 NEW: Get current voice ID synchronously
   */
  getCurrentVoiceId() {
    const preference = this.getCurrentVoicePreference();
    return preference?.voiceId || 'alex'; // Default to alex
  }

  /**
   * 🔥 NEW: Get current voice name synchronously
   */
  getCurrentVoiceName() {
    const preference = this.getCurrentVoicePreference();
    return preference?.name || 'Alex';
  }

  /**
   * Update voice preference (alias for saveVoicePreference)
   */
  updateVoicePreference(voiceId) {
    return this.saveVoicePreference(voiceId);
  }

  /**
   * Get voice stats (how long user has been using this voice)
   */
  getVoiceStats() {
    const preference = this.getCurrentVoicePreference();
    if (!preference?.selectedAt) return null;
    
    const selectedDate = new Date(preference.selectedAt);
    const daysSince = Math.floor((Date.now() - selectedDate.getTime()) / (1000 * 60 * 60 * 24));
    
    return {
      voiceId: preference.voiceId,
      voiceName: preference.name,
      selectedAt: selectedDate,
      daysSince,
      isNewUser: daysSince === 0
    };
  }

  /**
   * Get theme-specific styling based on current voice
   */
  getCurrentThemeColors() {
    const preference = this.getCurrentVoicePreference();
    if (!preference) return this.voiceConfigs.alex; // Default
    
    return {
      primary: preference.color,
      avatar: preference.avatar,
      theme: this.voiceConfigs[preference.voiceId]?.theme || 'professional'
    };
  }

  /**
   * Export voice preference for backup/sync
   */
  exportPreference() {
    const preference = this.getCurrentVoicePreference();
    if (!preference) return null;
    
    return {
      ...preference,
      exportedAt: new Date().toISOString(),
      appVersion: '1.0'
    };
  }

  /**
   * Import voice preference from backup
   */
  async importPreference(importData) {
    try {
      if (!importData.voiceId || !this.voiceConfigs[importData.voiceId]) {
        throw new Error('Invalid voice preference data');
      }
      
      const preference = {
        voiceId: importData.voiceId,
        name: importData.name,
        configId: importData.configId,
        selectedAt: importData.selectedAt || new Date().toISOString(),
        version: '1.0',
        importedAt: new Date().toISOString()
      };
      
      // Save to localStorage immediately
      localStorage.setItem('preptune-voice-preference', JSON.stringify(preference));
      
      // Save to Firestore in background
      await this.saveToFirestore(preference);
      
      console.log('✅ Voice preference imported successfully');
      return preference;
    } catch (error) {
      console.error('Error importing voice preference:', error);
      throw error;
    }
  }

  /**
   * Migrate localStorage preference to Firestore (called during initialization)
   */
  async migrateLocalStoragePreference() {
    try {
      const localPreference = this.getCurrentVoicePreference();
      if (localPreference && auth.currentUser) {
        await this.saveToFirestore({
          voiceId: localPreference.voiceId,
          name: localPreference.name,
          configId: localPreference.configId,
          selectedAt: localPreference.selectedAt,
          version: localPreference.version || '1.0'
        });
        
        console.log('✅ Voice preference migrated from localStorage to Firestore');
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error migrating voice preference:', error);
      return false;
    }
  }

  /**
   * Sync cached preference with Firestore
   */
  async syncWithFirestore() {
    try {
      const firestorePreference = await this.loadFromFirestore();
      return !!firestorePreference;
    } catch (error) {
      console.error('Error syncing with Firestore:', error);
      return false;
    }
  }
}

export default VoicePreferenceService;